import { useEffect, useMemo, useState } from 'react'
import { listerLivreursAssignables, pointDuMarchand, type LivreurAssignable } from '../api/dispatch'
import {
  ageMinutes,
  libelleDistance,
  mentionAnciennete,
  sansLeClient,
  trierParDistance,
} from '../api/livreursAssignables'
import { formaterNote } from '../api/notes'
import type { Commande } from '../api/types'
import { Modale } from './Modale'

/**
 * Choisir le livreur d'une commande.
 *
 * La liste ne contient que des livreurs ACTIFS (parcours d'intégration terminé) ET
 * DISPONIBLES, classés du plus proche au plus loin du marchand quand leur position
 * est connue. ⚠️ Depuis la migration 0047 le GPS ne tourne qu'en course : un livreur
 * qui attend n'a souvent aucune position récente — la distance est alors donnée
 * avec l'âge du relevé, ou « position inconnue ».
 *
 * « Premier disponible » reste l'option rapide : un clic, sans choisir.
 * L'affectation elle-même est faite par `set-order-status` (claim atomique) : si un
 * autre dispatcher ou un livreur a pris la commande entre-temps, l'erreur remonte
 * en phrase.
 */
export function ChoixLivreur({
  commande,
  surFermer,
  surChoisi,
  surPremier,
}: {
  commande: Commande | null
  surFermer: () => void
  surChoisi: (livreurId: string) => Promise<void>
  surPremier: () => Promise<void>
}) {
  const [liste, setListe] = useState<LivreurAssignable[] | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [occupe, setOccupe] = useState(false)
  const id = commande?.id ?? null

  useEffect(() => {
    setListe(null)
    setErreur(null)
    if (!id) return
    let vivant = true
    listerLivreursAssignables()
      .then((l) => vivant && setListe(l))
      .catch((e: unknown) => vivant && setErreur(e instanceof Error ? e.message : 'Livreurs illisibles.'))
    return () => {
      vivant = false
    }
  }, [id])

  const origine = commande ? pointDuMarchand(commande) : null
  // Le client de la commande n'est jamais candidat, même s'il est livreur (0053).
  const clientId = commande?.clientId ?? null
  const triee = useMemo(
    () => trierParDistance(sansLeClient(liste ?? [], clientId), origine),
    [liste, origine, clientId],
  )

  async function agir(action: () => Promise<void>) {
    setOccupe(true)
    setErreur(null)
    try {
      await action()
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Affectation impossible.')
    } finally {
      setOccupe(false)
    }
  }

  return (
    <Modale
      titre={commande ? `Assigner un livreur — ${commande.ref ?? ''}` : ''}
      ouverte={Boolean(commande)}
      surFermer={surFermer}
      large
    >
      {commande && (
        <>
          <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--n700)' }}>
            Retrait chez <b>{commande.marchandNom}</b>.{' '}
            {origine
              ? 'Les livreurs sont classés par distance à ce point de retrait (à vol d’oiseau).'
              : 'La position de ce marchand n’est pas renseignée : pas de distance, classement par nom.'}
          </p>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
            <button
              className="btn btn-accent btn-sm"
              disabled={occupe || liste === null || triee.length === 0}
              onClick={() => void agir(surPremier)}
              title="Affecte le premier livreur actif et disponible de la liste, sans choisir"
            >
              Premier disponible (rapide)
            </button>
            <span style={{ fontSize: 12, color: 'var(--n400)' }}>
              {liste === null ? 'Chargement…' : `${triee.length} livreur${triee.length > 1 ? 's' : ''} actif${triee.length > 1 ? 's' : ''} et disponible${triee.length > 1 ? 's' : ''}`}
            </span>
          </div>

          {erreur && <p style={{ color: 'var(--danger)', fontSize: 12.5, margin: '0 0 10px' }}>{erreur}</p>}

          <table>
            <thead>
              <tr>
                <th>Livreur</th>
                <th>Véhicule</th>
                <th>Distance</th>
                <th>Note</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {liste !== null && triee.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', color: 'var(--n400)', padding: 18 }}>
                    Aucun livreur actif et disponible pour le moment.
                  </td>
                </tr>
              )}
              {triee.map((l) => {
                const age = mentionAnciennete(ageMinutes(l.positionLe))
                return (
                  <tr key={l.id}>
                    <td>
                      <b>{l.nom}</b>
                      {l.telephone && <div style={{ fontSize: 11.5, color: 'var(--n400)' }}>{l.telephone}</div>}
                    </td>
                    <td>{l.vehicule || '—'}</td>
                    <td>
                      {libelleDistance(l.distanceKm)}
                      {l.distanceKm !== null && age && (
                        <div style={{ fontSize: 11, color: 'var(--n400)' }}>{age}</div>
                      )}
                    </td>
                    <td>{l.note !== null ? `${formaterNote(l.note)}/5` : '—'}</td>
                    <td>
                      <button
                        className="btn btn-outline btn-sm"
                        disabled={occupe}
                        onClick={() => void agir(() => surChoisi(l.id))}
                      >
                        Choisir
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </>
      )}
    </Modale>
  )
}
